import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import { usePagination } from 'hooks/pagination';
import {
  PaginationData,
  Resource,
  ResourceInventoryEntry,
  ResourceInventorySummary,
} from 'constants/interface';
import { Search, Table } from 'UI';
import { UnitMapping } from 'constants/unit';
import {
  cleanResourceEntriesAction,
  getResourceEntriesListAction,
  isLoadingResourceEntriesSelector,
  resourceEntriesEntitiesSelector,
  resourceEntriesIdsSelector,
  resourceEntriesRemoteTotalSelector,
} from 'state/slices/resource';
import {
  InventoryEntryTypeEnum,
  // InventoryEntryTypeMapping,
  InventoryTypeEnum,
} from 'constants/inventory';
import { useParams } from 'react-router';
import { ResourceCategoryUrlMapping } from 'constants/resource';
import InventoryEntryTypeLabel from 'components/InventoryEntryTypeLabel';

import {
  SearchContainer,
} from './style';

interface EntriesViewProps {
  shouldReload: boolean
  onReloadFinish(): void
}

interface ResourceInventoryRouteParams {
  categoryUrl: string
  organizationId: string
}

const EntriesView = ({
  shouldReload,
  onReloadFinish,
}: EntriesViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(resourceEntriesRemoteTotalSelector);

  const entriesEntities = useSelector(resourceEntriesEntitiesSelector);
  const entriesIds = useSelector(resourceEntriesIdsSelector);
  const isRemoteLoading = useSelector(isLoadingResourceEntriesSelector);

  const {
    categoryUrl,
    organizationId: activeOrganizationId,
  } = useParams<ResourceInventoryRouteParams>();
  const activeCategory = ResourceCategoryUrlMapping[categoryUrl];

  const entries = entriesIds.map((entryId) => {
    const entity = entriesEntities[entryId];
    if (!entity) return undefined;
    const tableEntry: ResourceInventoryEntry & {
      key?: string,
    } = {
      ...entity,
      key: entity.id,
    };
    return tableEntry;
  }).filter((tableEntry): tableEntry is ResourceInventoryEntry => !!tableEntry);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    // const orderByMapping: Record<string, string> = {
    //   owner: 'organization',
    // };
    // const orderBy = orderByMapping[paginationData.orderBy || ''] || paginationData.orderBy;
    dispatch(getResourceEntriesListAction({
      ...paginationData,
      inventoryType: InventoryTypeEnum.ORGANIZATION,
      category: activeCategory,
      organization: activeOrganizationId,
    }));
  };
  const pagination = usePagination<ResourceInventoryEntry>(
    remoteFetch,
    {
      filter: {
        type: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanResourceEntriesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  // const typeFilter = pageData.filter.type ?? null;

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar transação" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={entries}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Data"
          key="entryDate"
          dataIndex="entryDate"
          render={(entryDate: string) => {
            const date = new Date(entryDate);
            const formattedDate = date.toLocaleDateString('pt-br');
            return formattedDate;
          }}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'entryDate' ? pageData.order : undefined}
        />
        <Table.Column
          title="Tipo de transação"
          key="type"
          dataIndex="type"
          // render={(type: InventoryEntryTypeEnum) => {
          //   const formattedType = InventoryEntryTypeMapping[type].name;
          //   // TODO: maybe use icons
          //   return formattedType;
          // }}
          render={(type: InventoryEntryTypeEnum, record: ResourceInventoryEntry) => (
            <InventoryEntryTypeLabel entry={record} />
          )}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'type' ? pageData.order : undefined}
          // filters={Object.values(InventoryEntryTypeEnum).map((type) => ({
          //   text: InventoryEntryTypeMapping[type].name,
          //   value: type,
          // }))}
          // filterMultiple={false}
          // filteredValue={typeFilter !== null ? [typeFilter] : null}
        />
        <Table.Column
          title="Item"
          key="resource"
          dataIndex="resource"
          render={(resource: Omit<
            Resource,
            'createdAt' |
            'updatedAt' |
            'createdBy' |
            'updatedBy'
            >) => resource.name}
          // sorter={sorter}
          // sortOrder={pageData.orderBy === 'resource' ? pageData.order : undefined}
        />
        <Table.Column
          title="Quantidade"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number, record: ResourceInventorySummary) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} ${UnitMapping[record.resource.unit].name}`;
            return content;
          }}
        />
        <Table.Column
          title="Taxa"
          key="price"
          dataIndex="price"
          render={(price: number, record: ResourceInventoryEntry) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((price / 100) / record.quantity);
            return currencyValue;
          }}
        />
        <Table.Column
          title="Valor"
          key="price"
          dataIndex="price"
          render={(price: number) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((price / 100));
            return currencyValue;
          }}
        />
        <Table.Column
          title="Saldo"
          key="balanceDiff"
          dataIndex="balanceDiff"
          render={(balanceDiff: number, record: ResourceInventorySummary) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(balanceDiff);
            const content = `${formattedQuantity} ${UnitMapping[record.resource.unit].name}`;
            return content;
          }}
        />
      </Table>
    </>
  );
};

export default EntriesView;
